import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CertifierDetails, getCertifierDetails, saveCertifierDetails } from '../../api/certifications';
import { BlockSkeleton } from './CertificationSkeleton';
import './CertificationPage.css';

// What goes into every summary for the Ordem: "Dr. Name, Advogado, titular da
// cédula profissional n.º …, com escritório em …".
const CertifierDetailsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [details, setDetails] = useState<CertifierDetails | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      try {
        const data = await getCertifierDetails(await currentUser.getIdToken());
        if (!cancelled) setDetails({ ...data, salutation: data.salutation || 'Dr.' });
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load your details.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const change = (key: keyof CertifierDetails) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setSaved(false);
    setDetails(current => (current ? { ...current, [key]: e.target.value } : current));
  };

  const complete = details && [details.name, details.title, details.cedula, details.address].every(v => v.trim());

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !details) return;
    setSaving(true);
    setError('');
    try {
      setDetails(await saveCertifierDetails(await currentUser.getIdToken(), details));
      setSaved(true);
    } catch (err: any) {
      setError(err.message || 'Could not save your details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="certification-page">
      <h1>My details</h1>
      <p className="certification-intro">
        These go into the text you paste on the Ordem portal, so enter them as the Ordem has them.
      </p>
      {error && <div className="certification-error" role="alert">{error}</div>}
      {!error && !details && <BlockSkeleton lines={5} />}
      {details && (
        <form className="certification-details" onSubmit={save}>
          <label>
            Salutation
            <select value={details.salutation} onChange={change('salutation')}>
              <option value="Dr.">Dr.</option>
              <option value="Dra.">Dra.</option>
            </select>
          </label>
          <label>
            Full name
            <input value={details.name} onChange={change('name')} maxLength={120} />
          </label>
          <label>
            Professional title
            <input value={details.title} onChange={change('title')} maxLength={60} placeholder="Advogado" />
          </label>
          <label>
            Cédula profissional
            <input value={details.cedula} onChange={change('cedula')} maxLength={20} placeholder="12345L" />
          </label>
          <label>
            Office address
            <input value={details.address} onChange={change('address')} maxLength={200} />
          </label>
          <button type="submit" className="certification-btn certification-btn--primary" disabled={!complete || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span role="status" className="certification-saved">Saved</span>}
        </form>
      )}
    </div>
  );
};

export default CertifierDetailsPage;
